import OrderableImagePlaceholderIcon from '@assets/icons/orderable-image-placeholder.svg';
import React, { useState } from 'react';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import {
  ImageStyle,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';

export const STANDARD_IMAGE_SIZE = 56;
const HUGE_IMAGE_SIZE = 136;
const LARGE_IMAGE_SIZE = 90;
const MEDIUM_IMAGE_SIZE = 48;
const SMALL_IMAGE_SIZE = 40;

const BADGE_SIZE = {
  [ListDisplaySize.Small]: 16,
  [ListDisplaySize.Condensed]: 24,
  [ListDisplaySize.Large]: 32,
  [ListDisplaySize.Medium]: 20,
  [ListDisplaySize.Huge]: 32,
  [ListDisplaySize.Standard]: 24,
};

export const DISPLAY_SIZE_TO_IMAGE_SIZE = {
  [ListDisplaySize.Small]: SMALL_IMAGE_SIZE,
  [ListDisplaySize.Condensed]: STANDARD_IMAGE_SIZE,
  [ListDisplaySize.Large]: LARGE_IMAGE_SIZE,
  [ListDisplaySize.Medium]: MEDIUM_IMAGE_SIZE,
  [ListDisplaySize.Huge]: HUGE_IMAGE_SIZE,
  [ListDisplaySize.Standard]: STANDARD_IMAGE_SIZE,
};

interface Props {
  alt: string;
  badge?: React.ReactNode;
  badgeStyle?: StyleProp<ViewStyle>;
  hidePlaceholderImages: boolean;
  imageSize?: number;
  imageURLs: string[];
  listDisplaySize?: ListDisplaySize;
  style?: StyleProp<ImageStyle>;
}

export const OrderableItemListItemImage: React.FC<Props> = ({
  alt,
  badge,
  badgeStyle,
  hidePlaceholderImages,
  imageSize,
  imageURLs,
  listDisplaySize = ListDisplaySize.Standard,
  style,
}) => {
  const finalImageSize =
    imageSize ?? DISPLAY_SIZE_TO_IMAGE_SIZE[listDisplaySize];
  const badgeSize = BADGE_SIZE[listDisplaySize];
  const imageURL = imageURLs[0];

  const [hasError, setHasError] = useState(false);

  return (
    <>
      {imageURL && !hasError ? (
        <CachedImage
          alt={alt}
          contentFit="contain"
          onError={() => setHasError(true)}
          recyclingKey={imageURL}
          source={{ uri: imageURL }}
          style={[
            { height: finalImageSize, width: finalImageSize },
            style,
            styles.image,
          ]}
          testID="orderable-item-image"
        />
      ) : !hidePlaceholderImages ? (
        <View style={styles.image}>
          <OrderableImagePlaceholderIcon
            height={finalImageSize}
            testID="orderable-item-image-placeholder"
            width={finalImageSize}
          />
        </View>
      ) : null}
      {badge ? (
        <View
          style={[
            styles.badge,
            {
              height: badgeSize,
              width: badgeSize,
            },
            badgeStyle,
          ]}
        >
          {badge}
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  badge: {
    borderRadius: 4,
    left: 4,
    position: 'absolute',
    top: 4,
  },
  image: {
    alignSelf: 'center',
  },
});
